import { api, resolvePagination } from './api';

export const isValidContinuousDataRange = (startTimestamp, endTimestamp) => {
    const start = Date.parse(startTimestamp);
    const end = Date.parse(endTimestamp);
    return Number.isFinite(start) && Number.isFinite(end) && start <= end;
};

export const fetchContinuousDataPage = async ({
    patient,
    dtype,
    startTimestamp,
    endTimestamp,
    pageLimit = 150,
    pageToken,
    signal
}) => {
    if (
        typeof patient !== 'string' ||
        !patient.trim() ||
        typeof dtype !== 'string' ||
        !dtype.trim() ||
        !isValidContinuousDataRange(startTimestamp, endTimestamp)
    ) {
        throw new Error('A patient, data type and valid timestamp range are required.');
    }
    if (!Number.isInteger(pageLimit) || pageLimit < 1 || pageLimit > 150) {
        throw new Error('Continuous data page limit must be between 1 and 150.');
    }

    const response = await api.get('/cheart/record/continuous', {
        headers: {
            ...resolvePagination(pageToken, pageLimit),
            'CHEART-QUERY-PATIENT': patient,
            'CHEART-QUERY-DATA-POST-TYPE': dtype,
            'CHEART-QUERY-TIMESTAMP-START': startTimestamp,
            'CHEART-QUERY-TIMESTAMP-END': endTimestamp,
            'CHEART-QUERY-ORDER': 'ASC'
        },
        ...(signal ? { signal } : {})
    });
    const countHeader = response.headers['pagination-count'];
    const totalCount = Number(countHeader);
    const table = response.data;
    if (
        !Array.isArray(table?.data) ||
        countHeader == null ||
        String(countHeader).trim() === '' ||
        !Number.isSafeInteger(totalCount) ||
        totalCount < 0 ||
        table.data.length > totalCount
    ) {
        throw new Error('Invalid continuous data response or pagination count.');
    }

    const columns = table.columns;
    if (
        !(columns === null && table.data.length === 0) &&
        (!Array.isArray(columns) ||
            columns.length === 0 ||
            columns.some((column) => typeof column !== 'string' || !column.trim()) ||
            new Set(columns).size !== columns.length ||
            table.data.some(
                (row) => !Array.isArray(row) || row.length !== columns.length
            ))
    ) {
        throw new Error('Invalid continuous data columns or rows.');
    }
    return {
        columns,
        data: table.data,
        totalCount,
        pageToken: response.headers['pagination-token'] || null
    };
};

export const fetchContinuousData = async (options) => {
    const data = [];
    let columns = null;
    const tokens = new Set();
    let pageToken;
    let totalCount;
    do {
        const page = await fetchContinuousDataPage({ ...options, pageToken });
        if (totalCount !== undefined && page.totalCount !== totalCount) {
            throw new Error('Inconsistent continuous data pagination count.');
        }
        totalCount = page.totalCount;
        if (page.columns !== null) {
            if (
                columns !== null &&
                (columns.length !== page.columns.length ||
                    columns.some((column, index) => column !== page.columns[index]))
            )
                throw new Error('Inconsistent continuous data columns across pages.');
            columns = page.columns;
        }
        data.push(...page.data);
        if (data.length > totalCount)
            throw new Error('Invalid continuous data pagination count.');
        // The backend returns a token even on the last page.
        if (data.length === totalCount) {
            return { columns, data, totalCount };
        }
        if (!page.data.length || !page.pageToken || tokens.has(page.pageToken)) {
            throw new Error('Incomplete continuous data pagination.');
        }
        tokens.add(page.pageToken);
        pageToken = page.pageToken;
    } while (data.length < totalCount);
};
